import React, { useEffect, useState } from 'react';
import { IonHeader, IonToolbar, IonContent, IonSegmentButton, IonSegment, IonPage, IonButtons, IonBackButton, IonButton, IonIcon, IonText, IonList, IonItem, IonLabel, IonListHeader, IonRow, IonCol, IonInput, IonFab, IonFabButton, IonBadge, IonToast, IonSpinner } from '@ionic/react';
import { connect } from '../data/connect';
import { withRouter, RouteComponentProps } from 'react-router';
import * as selectors from '../data/selectors';
import { starOutline, star, calendar, cloudDownload, thumbsUpOutline, thumbsUp, sendOutline, addOutline, informationCircleOutline, chatbubbleOutline, clipboardOutline } from 'ionicons/icons';
import { addFavorite, removeFavorite, loadConfData } from '../data/sessions/sessions.actions';
import { Session } from '../models/Schedule';
import SpeakerItem from '../components/SpeakerItem';
import { Speaker } from '../models/Speaker';
import { useHistory } from 'react-router';
import { Storage } from '@capacitor/storage';
import QuestionCard from '../components/QuestionCard';
import SessionHeader from '../components/SessionHeader';
import QuestionInput from '../components/QuestionInput';
import { isOnline } from '../utils/offlineManager';
import { isIOSDevice } from '../utils/deviceDetection';
import './SessionDetail.css';
// import { parse, format, isValid, sub } from 'date-fns';
import { es } from 'date-fns/locale'; // Importamos el locale en español para `date-fns`
import { getDefaultAvatar } from '../utils/defaultAvatar';
import UserAvatar from '../components/UserAvatar';
import SessionSurvey from '../components/SessionSurvey';
import { SessionSurveyConfig, sessionSurveyService } from '../services/SessionSurveyService';
import FavoritesService from '../services/FavoritesService';

import { firebase } from '../config/firebase.config';

interface OwnProps extends RouteComponentProps<{ id: string }> { };

export interface Question {
  id?: string;
  title: string;
  user?: string;
  eventcode?: string;
  createdAt?: any;
  creado?: any;
  likesCount: number;
  yaDioLike?: boolean;  // Nuevo campo para saber si el usuario ya dio like
  isHidden?: boolean;   // Nuevo campo para preguntas ocultas (desde Firebase)
  isAnswered?: boolean; // Nuevo campo para preguntas respondidas (desde Firebase)
}

interface StateProps {
  session?: Session;
  favoriteSessions: string[];
  speakers: Speaker[];
  loading?: boolean;
};

interface DispatchProps {
  addFavorite: typeof addFavorite;
  removeFavorite: typeof removeFavorite;
  loadConfData: typeof loadConfData;
}

type SessionDetailProps = OwnProps & StateProps & DispatchProps;

const SessionDetail: React.FC<SessionDetailProps> = ({ session, addFavorite, removeFavorite, favoriteSessions, speakers, loading, loadConfData }) => {
  const [retryAttempts, setRetryAttempts] = useState(0);
  const maxRetries = 3;

  // Debug logging
  useEffect(() => {
    console.log('SessionDetail: Props recibidas:', {
      session: session ? { id: session.id, name: session.name } : null,
      favoriteSessions: favoriteSessions.length,
      speakers: speakers.length,
      loading
    });
  }, [session, favoriteSessions, speakers, loading]);

  // Cargar datos de la conferencia si no están disponibles (por ejemplo, al recargar la página)
  useEffect(() => {
    if (!session && !loading && retryAttempts < maxRetries) {
      console.log('SessionDetail: Sesión no encontrada, intentando cargar datos...', retryAttempts + 1);
      setRetryAttempts(prev => prev + 1);
      loadConfData();
    }
  }, [session, loading, retryAttempts, maxRetries, loadConfData]);

  // Retry mechanism si no se encuentra la sesión
  useEffect(() => {
    if (!loading && !session && retryAttempts < maxRetries) {
      console.log(`SessionDetail: Intento ${retryAttempts + 1} de ${maxRetries} para cargar datos`);
      const timer = setTimeout(() => {
        setRetryAttempts(prev => prev + 1);
        // Recargar datos sin recargar la página
        console.log('SessionDetail: Ejecutando loadConfData para recargar datos...');
        loadConfData();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [session, loading, retryAttempts, loadConfData]);
  const history = useHistory();

  const [question, setQuestion] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedTab, setSelectedTab] = useState<'details' | 'questions' | 'survey'>('details');
  const [showQuestionInput, setShowQuestionInput] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [isLogisticSupport, setIsLogisticSupport] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>('');
  const [eventCode, setEventCode] = useState<string>('');
  const [sessionSurvey, setSessionSurvey] = useState<SessionSurveyConfig | null>(null);
  let sessionConfirmed = session ? session.id : "";
  let contador = 0;

  useEffect(() => {
    const loadSessionContext = async () => {
      const [storedUserId, storedEventCode] = await Promise.all([
        Storage.get({ key: 'userid' }),
        Storage.get({ key: 'eventcode' })
      ]);
      setUserId(storedUserId.value || '');
      setEventCode(storedEventCode.value || '');
    };
    loadSessionContext();

    // Verificar rol del usuario
    checkUserRole();
  }, []);

  // Función para verificar si el usuario tiene rol de "APOYO LOGISTICO"
  const checkUserRole = async () => {
    try {
      const userOtherData = await Storage.get({ key: 'userother' });
      if (userOtherData.value) {
        // El formato es "company&&&role"
        const parts = userOtherData.value.split('&&&');
        const role = parts.length > 1 ? parts[1] : '';
        setIsLogisticSupport(role.trim().toUpperCase() === 'APOYO LOGISTICO');
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setIsLogisticSupport(false);
    }
  };

  useEffect(() => {
    if (!eventCode || !sessionConfirmed) {
      setSessionSurvey(null);
      return;
    }

    return sessionSurveyService.subscribeSurvey(
      eventCode,
      sessionConfirmed,
      setSessionSurvey,
      error => {
        console.error('No se pudo cargar la encuesta de la sesión:', error);
        setSessionSurvey(null);
      }
    );
  }, [eventCode, sessionConfirmed]);

  const showSurveyTab = isLogisticSupport || Boolean(sessionSurvey?.active);
  // Si el moderador desactiva la encuesta mientras un participante la está
  // viendo, mostrar Detalles en el mismo render en que desaparece la pestaña.
  const visibleTab = selectedTab === 'survey' && !showSurveyTab ? 'details' : selectedTab;

  useEffect(() => {
    if (selectedTab === 'survey' && !showSurveyTab) {
      setSelectedTab('details');
    }
  }, [selectedTab, showSurveyTab]);

  // Consulta inicial para obtener las preguntas junto con la información si el usuario ya dio "like"
  useEffect(() => {
    if (sessionConfirmed) {
      const unsubscribe = firebase.firestore().collection('questions')
        .where('sessionid', '==', sessionConfirmed)
        .onSnapshot(async (snapshot) => {
          const questionsData: Question[] = await Promise.all(
            snapshot.docs.map(async (doc) => {
              const question = doc.data() as Question;
              question.id = doc.id;

              // Verificar si el usuario ya dio "like" en la subcolección de 'likes'
              if (userId) {
                const likeSnapshot = await firebase.firestore()
                  .collection('questions')
                  .doc(doc.id)
                  .collection('likes')
                  .where('userId', '==', userId)
                  .get();
                question.yaDioLike = !likeSnapshot.empty;
              } else {
                question.yaDioLike = false;
              }
              return question;
            })
          );
          const sortedQuestions = questionsData.sort((questionA, questionB) => {
            const likesDifference = (questionB.likesCount || 0) - (questionA.likesCount || 0);
            if (likesDifference !== 0) return likesDifference;

            const questionATime = questionA.creado?.toMillis?.() || 0;
            const questionBTime = questionB.creado?.toMillis?.() || 0;
            return questionBTime - questionATime;
          });
          setQuestions(sortedQuestions);
        }, (error) => {
          console.error('Error al consultar las preguntas de la sesión:', error);
          setToastMessage('No se pudieron cargar las preguntas de esta sesión.');
          setShowToast(true);
        });
      return () => unsubscribe();
    }
  }, [sessionConfirmed, userId]);

  // Función para encontrar el ID de un conferencista por su nombre
  const findSpeakerIdByName = (name: string): string | number | null => {
    if (!speakers || !name || name === "_" || name === "") {
      console.log('SessionDetail: Búsqueda cancelada - speakers:', !!speakers, 'name:', name);
      return null;
    }

    console.log('🔍 findSpeakerIdByName:', {
      searchName: name,
      searchNameTrimmed: name.toLowerCase().trim(),
      totalSpeakers: speakers.length,
      firstFewSpeakers: speakers.slice(0, 3).map((s: any) => ({ id: s.id, name: s.name }))
    });

    // Búsqueda más robusta con trim en ambos lados
    const searchName = name.toLowerCase().trim();
    const speaker = speakers.find(s => s.name.toLowerCase().trim() === searchName);
    console.log('🔍 findSpeakerIdByName result:', speaker ? { id: speaker.id, name: speaker.name } : 'NOT FOUND');
    return speaker ? speaker.id : null;
  };

  // Función para navegar al detalle del conferencista
  const goToSpeakerDetail = (speakerName: string) => {
    console.log('SessionDetail: Intentando navegar a speaker:', speakerName);
    const speakerId = findSpeakerIdByName(speakerName);
    console.log('SessionDetail: Speaker ID encontrado:', speakerId);

    if (speakerId) {
      const targetUrl = `/tabs/speakers/${speakerId}`;
      console.log('SessionDetail: Navegando a:', targetUrl);
      history.push(targetUrl);
    } else {
      console.warn('SessionDetail: No se encontró speaker con nombre:', speakerName);
    }
  };

  // Ejemplo de uso:
  /*const fechaHoraEnEspanol = "Lunes 25 Septiembre 2024T1:00 p.m.";
  const sessionTitle = "Reunión Importante";
  const location = "Auditorio Principal";
  const durationInMinutes = 60;

  try {
    generarYDescargarICS(fechaHoraEnEspanol, sessionTitle, location, durationInMinutes);
  } catch (error) {
    //console.error(error.message);
  }*/


  const handleSubmitQuestion = async (questionText: string): Promise<boolean> => {
    try {
      // Verificar conexión a internet
      if (!isOnline()) {
        setToastMessage('No hay conexión a internet. Su pregunta no puede ser enviada.');
        setShowToast(true);
        return false;
      }

      // Obtener el ID del usuario
      const currentUserId = (await Storage.get({ key: 'userid' })).value || '';
      const currentEventCode = (await Storage.get({ key: 'eventcode' })).value || '';

      if (!currentUserId || !currentEventCode) {
        setToastMessage('Debe iniciar sesión para enviar preguntas.');
        setShowToast(true);
        return false;
      }

      // Enviar la pregunta a Firestore
      await firebase.firestore().collection('questions').add({
        title: questionText,
        userid: currentUserId,
        sessionid: sessionConfirmed,
        eventcode: currentEventCode,
        likesCount: 0,
        creado: firebase.firestore.FieldValue.serverTimestamp(),
      });

      // Actualizar la UI
      setQuestion("");
      setSelectedTab('questions'); // Cambiar a la pestaña de preguntas

      return true;
    } catch (error) {
      console.error('Error al enviar la pregunta:', error);
      return false;
    }
  };

  const incrementarLikes = async (questionId: string, likesCountActual: number) => {
    try {
      // Obtener el ID del usuario
      const currentUserId = (await Storage.get({ key: 'userid' })).value || '';
      if (!currentUserId) {
        setToastMessage('Debe iniciar sesión para dar like a las preguntas.');
        setShowToast(true);
        return;
      }

      // Verificar si el usuario ya dio like directamente en Firestore
      const likeSnapshot = await firebase.firestore()
        .collection('questions')
        .doc(questionId)
        .collection('likes')
        .where('userId', '==', currentUserId)
        .get();

      // Si ya dio like, mostrar mensaje y salir
      if (!likeSnapshot.empty) {
        setToastMessage('Ya has dado like a esta pregunta anteriormente.');
        setShowToast(true);
        return;
      }

      // Incrementar el contador de likes usando una transacción para evitar condiciones de carrera
      await firebase.firestore().runTransaction(async (transaction) => {
        // Obtener el documento actual para tener el contador de likes más actualizado
        const questionDoc = await transaction.get(
          firebase.firestore().collection('questions').doc(questionId)
        );

        if (!questionDoc.exists) {
          throw new Error('La pregunta ya no existe');
        }

        // Obtener el contador actual de likes
        const currentLikesCount = questionDoc.data()?.likesCount || 0;

        // Actualizar el contador de likes
        transaction.update(
          firebase.firestore().collection('questions').doc(questionId),
          { likesCount: currentLikesCount + 1 }
        );
      });

      // Añadir el usuario a la subcolección 'likes'
      await firebase.firestore().collection('questions').doc(questionId).collection('likes').add({
        userId: currentUserId,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      });

      // Actualizar el estado local para reflejar el cambio de 'yaDioLike'
      setQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.id === questionId ? { ...q, likesCount: likesCountActual + 1, yaDioLike: true } : q
        )
      );
    } catch (error) {
      console.error('Error al incrementar los likes:', error);
      setToastMessage('Ocurrió un error al dar like a la pregunta. Por favor, intente nuevamente.');
      setShowToast(true);
    }
  };

  // Función para ocultar/mostrar una pregunta (solo para apoyo logístico)
  const handleHideQuestion = async (questionId: string) => {
    if (!isLogisticSupport) {
      setToastMessage('No tienes permisos para realizar esta acción');
      setShowToast(true);
      return;
    }

    try {
      const currentEventCode = (await Storage.get({ key: 'eventcode' })).value || '';
      const currentUserId = userId || (await Storage.get({ key: 'userid' })).value || '';
      if (!currentEventCode) {
        throw new Error('EVENT_REQUIRED');
      }

      // Obtener el estado actual de la pregunta
      const questionDoc = await firebase.firestore().collection('questions').doc(questionId).get();

      if (!questionDoc.exists) {
        setToastMessage('La pregunta no existe');
        setShowToast(true);
        return;
      }

      const currentData = questionDoc.data();
      const isCurrentlyHidden = currentData?.isHidden || false;

      // Actualizar el estado en Firebase
      await firebase.firestore().collection('questions').doc(questionId).update({
        isHidden: !isCurrentlyHidden,
        eventcode: currentData?.eventcode || currentEventCode,
        lastModified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: currentUserId
      });

      setToastMessage(isCurrentlyHidden ? 'Pregunta mostrada' : 'Pregunta ocultada');
      setShowToast(true);
    } catch (error) {
      console.error('Error updating question visibility:', error);
      setToastMessage('Error al actualizar la pregunta');
      setShowToast(true);
    }
  };

  // Función para marcar/desmarcar una pregunta como respondida (solo para apoyo logístico)
  const handleMarkAsAnswered = async (questionId: string, isAnswered: boolean) => {
    if (!isLogisticSupport) {
      setToastMessage('No tienes permisos para realizar esta acción');
      setShowToast(true);
      return;
    }

    try {
      const currentEventCode = (await Storage.get({ key: 'eventcode' })).value || '';
      const currentUserId = userId || (await Storage.get({ key: 'userid' })).value || '';
      if (!currentEventCode) {
        throw new Error('EVENT_REQUIRED');
      }

      // Actualizar el estado en Firebase
      await firebase.firestore().collection('questions').doc(questionId).update({
        isAnswered: isAnswered,
        eventcode: currentEventCode,
        lastModified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: currentUserId
      });

      setToastMessage(isAnswered ? 'Pregunta marcada como respondida' : 'Pregunta marcada como no respondida');
      setShowToast(true);
    } catch (error) {
      console.error('Error updating question status:', error);
      setToastMessage('Error al actualizar la pregunta');
      setShowToast(true);
    }
  };

  //console.log(session);

  // Mostrar loading si los datos están cargando
  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/schedule" />
            </IonButtons>
            <IonText>Cargando...</IonText>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <IonSpinner name="crescent" style={{ fontSize: '3rem', color: 'var(--ion-color-primary)' }} />
            <p style={{ marginTop: '20px', color: 'var(--ion-color-medium)' }}>
              Cargando información de la sesión...
            </p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!session) {
    console.error('SessionDetail: Sesión no encontrada');

    // Si está cargando, mostrar spinner
    if (loading) {
      return (
        <IonPage>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonBackButton defaultHref="/tabs/schedule" />
              </IonButtons>
              <IonText>Cargando sesión...</IonText>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <IonSpinner name="crescent" />
              <p style={{ marginTop: '20px' }}>Cargando información de la sesión...</p>
            </div>
          </IonContent>
        </IonPage>
      );
    }

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/schedule" text="Volver" />
            </IonButtons>
            <IonText>Sesión no encontrada</IonText>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <IonIcon
              icon={informationCircleOutline}
              style={{
                fontSize: '4rem',
                color: 'var(--ion-color-medium)',
                marginBottom: '20px'
              }}
            />
            <h2>Sesión no encontrada</h2>
            <p style={{ color: 'var(--ion-color-medium)' }}>
              La sesión que buscas no está disponible o ha sido eliminada.
            </p>
            {retryAttempts < maxRetries && (
              <p style={{ color: 'var(--ion-color-warning)', fontSize: '0.9rem' }}>
                Reintentando cargar datos... ({retryAttempts}/{maxRetries})
              </p>
            )}
            <div style={{ marginTop: '20px' }}>
              <IonButton
                expand="block"
                fill="solid"
                onClick={() => {
                  console.log('SessionDetail: Botón Reintentar presionado, ejecutando loadConfData...');
                  setRetryAttempts(0);
                  loadConfData();
                }}
                style={{ marginBottom: '12px', maxWidth: '300px', margin: '0 auto 12px' }}
              >
                Reintentar
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                routerLink="/tabs/schedule"
                style={{ maxWidth: '300px', margin: '0 auto' }}
              >
                Volver a la Agenda
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }


  const isFavorite = favoriteSessions.indexOf(session.id.toString()) > -1;
  const toggleFavorite = async() => {
    const sessionId = session.id.toString();
    try {
      const favoritesService = FavoritesService.getInstance();
      if (isFavorite) {
        await favoritesService.removeFavorite(sessionId);
        removeFavorite(sessionId);
      } else {
        await favoritesService.addFavorite(sessionId);
        addFavorite(sessionId);
      }
    } catch (error) {
      console.error('SessionDetail: No se pudo actualizar el favorito:', error);
      setToastMessage('No se pudo actualizar favoritos. Intente nuevamente.');
      setShowToast(true);
    }
  };

  return (
    <IonPage id="session-detail-page">
      <IonContent fullscreen>
        {/* Botón de regreso */}
        <IonButtons className="back-button-container">
          <IonBackButton defaultHref="/tabs/schedule" text="Volver" className="custom-back-button" />
        </IonButtons>

        {/* Encabezado de sesión */}
        <SessionHeader
          name={session.name}
          avatar="/assets/img/speakers/lion.jpg"
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          onAddToCalendar={() => {
            // Construir la URL base
            const baseUrl = `esimposio.com/miesimposio/appendpoints/generaICS.php?startTime=${encodeURIComponent(convertirFechaICS(session.date+"T"+session.timeStart))}&endTime=${encodeURIComponent(convertirFechaICS(session.date+"T"+session.timeEnd))}&sessionTitle=${encodeURIComponent(session.name)}&location=${encodeURIComponent(session.location)}`;

            // Usar protocolo diferente según la plataforma
            const calendarUrl = isIOSDevice() ? `webcal://${baseUrl}` : `https://${baseUrl}`;

            // Abrir en una nueva pestaña para evitar recargar la aplicación
            window.open(calendarUrl, '_blank');
          }}
          onAskQuestion={() => {
            // Verificar conexión a internet
            if (!isOnline()) {
              setToastMessage('No hay conexión a internet. No puede enviar preguntas en modo offline.');
              setShowToast(true);
              return;
            }

            // Mostrar el campo de preguntas
            setShowQuestionInput(true);
          }}
        />

        {/* Tabs de navegación */}
        <div className="session-tabs">
          <button
            className={`tab-button ${visibleTab === 'details' ? 'active' : ''}`}
            onClick={() => setSelectedTab('details')}
          >
            <IonIcon icon={informationCircleOutline} />
            <span>Detalles</span>
            <div className="tab-indicator"></div>
          </button>
          <button
            className={`tab-button ${visibleTab === 'questions' ? 'active' : ''}`}
            onClick={() => setSelectedTab('questions')}
          >
            <IonIcon icon={chatbubbleOutline} />
            <span>Preguntas</span>
            <IonBadge className="question-count">{questions.length}</IonBadge>
            <div className="tab-indicator"></div>
          </button>
          {showSurveyTab && (
            <button
              className={`tab-button ${visibleTab === 'survey' ? 'active' : ''}`}
              onClick={() => setSelectedTab('survey')}
            >
              <IonIcon icon={clipboardOutline} />
              <span>Encuesta</span>
              <div className="tab-indicator"></div>
            </button>
          )}
        </div>

        <div className="session-content">
          {/* Contenido de la pestaña de detalles */}
          {visibleTab === 'details' && (
            <div className="session-info">
              <IonText color="medium">
                <p className="session-time">{session.timeStart} a {session.timeEnd} | {session.location}</p>
              </IonText>

              {session.description && session.description.trim() && (
                <div className="session-description">
                  <h2 className="section-title">Descripción</h2>
                  <p>{session.description}</p>
                </div>
              )}

              {session.speakerNames && session.speakerNames.length > 0 && session.speakerNames.some(speaker => speaker && speaker.trim() !== '') && (
                <div className="session-speakers">
                  <h2 className="section-title">
                    {session.speakerNames.filter(speaker => speaker && speaker.trim() !== '').length === 1 ? 'Ponente' : 'Ponentes'}
                  </h2>
                  {session.speakerNames
                    .filter(speaker => speaker && speaker.trim() !== '')
                    .map((speaker, index) => {
                    const speakerId = findSpeakerIdByName(speaker);
                    const speakerData = speakers.find(s => s.name.toLowerCase().trim() === speaker.toLowerCase().trim());

                    // Debug temporal
                    console.log('🔍 SessionDetail speaker lookup:', {
                      speakerName: speaker,
                      speakerNameTrimmed: speaker.toLowerCase().trim(),
                      speakerData: speakerData ? {
                        name: speakerData.name,
                        profilePic: speakerData.profilePic,
                        id: speakerData.id
                      } : null,
                      totalSpeakers: speakers.length,
                      firstFewSpeakers: speakers.slice(0, 3).map(s => ({ name: s.name, id: s.id }))
                    });

                    console.log('🔍 SessionDetail speaker lookup:', {
                      speakerName: speaker,
                      speakerNameTrimmed: speaker.toLowerCase().trim(),
                      speakerId,
                      speakerData: speakerData ? {
                        name: speakerData.name,
                        profilePic: speakerData.profilePic,
                        id: speakerData.id
                      } : null,
                      totalSpeakers: speakers.length,
                      allSpeakerNames: speakers.slice(0, 5).map(s => s.name) // Solo primeros 5 para no saturar logs
                    });

                    return (
                      <div
                        key={index}
                        className="speaker-item"
                        onClick={() => speakerId && goToSpeakerDetail(speaker)}
                        style={{ cursor: speakerId ? 'pointer' : 'default' }}
                      >
                        <div className="speaker-avatar">
                          {speakerData && speakerData.profilePic ? (
                            <img
                              src={speakerData.profilePic}
                              alt={speaker}
                              style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                cursor: speakerId ? 'pointer' : 'default'
                              }}
                              onClick={() => speakerId && goToSpeakerDetail(speaker)}
                            />
                          ) : (
                            <div className="speaker-avatar-placeholder" style={{
                              width: '100px',
                              height: '100px',
                              borderRadius: '50%',
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '24px',
                              color: '#666',
                              cursor: speakerId ? 'pointer' : 'default'
                            }}
                            onClick={() => speakerId && goToSpeakerDetail(speaker)}
                            >
                              {speaker.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="speaker-info">
                          <h3 style={{
                            color: speakerId ? 'var(--ion-color-primary)' : 'inherit',
                            textDecoration: speakerId ? 'underline' : 'none'
                          }}>
                            {speaker}
                            {speakerId && <span style={{ fontSize: '0.8em', marginLeft: '5px' }}>→</span>}
                          </h3>
                          {speakerData && speakerData.title && (
                            <p style={{
                              margin: '4px 0 0 0',
                              fontSize: '14px',
                              color: '#666',
                              fontStyle: 'italic'
                            }}>
                              {speakerData.title}
                            </p>
                          )}
                          {speakerData && speakerData.logo && (
                            <div style={{ marginTop: '8px' }}>
                              <img
                                src={speakerData.logo}
                                alt={`${speaker} logo`}
                                style={{
                                  maxHeight: '50px',
                                  maxWidth: '100px',
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Contenido de la pestaña de preguntas */}
          {visibleTab === 'questions' && (
            <div className="questions-container">
              <div className="questions-header">
                <h2 className="section-title">
                  {(() => {
                    const visibleQuestions = questions.filter((question) => {
                      // Si no es usuario de apoyo logístico, ocultar las preguntas marcadas como ocultas
                      if (!isLogisticSupport && question.isHidden) {
                        return false;
                      }
                      return true;
                    });
                    return visibleQuestions.length > 0 ? "Preguntas" : "No hay preguntas aún";
                  })()}
                </h2>
              </div>

              {questions
                .filter((question) => {
                  // Si no es usuario de apoyo logístico, ocultar las preguntas marcadas como ocultas
                  if (!isLogisticSupport && question.isHidden) {
                    return false;
                  }
                  return true;
                })
                .map((question) => {
                  return (
                    <QuestionCard
                      key={question.id}
                      question={question}
                      onLike={incrementarLikes}
                      onHide={isLogisticSupport ? handleHideQuestion : undefined}
                      onMarkAsAnswered={isLogisticSupport ? handleMarkAsAnswered : undefined}
                      isHidden={question.isHidden || false}
                      isAnswered={question.isAnswered || false}
                    />
                  );
                })}

              {(() => {
                const visibleQuestions = questions.filter((question) => {
                  // Si no es usuario de apoyo logístico, ocultar las preguntas marcadas como ocultas
                  if (!isLogisticSupport && question.isHidden) {
                    return false;
                  }
                  return true;
                });

                return visibleQuestions.length === 0 && (
                  <div className="no-questions">
                    <p>
                      {questions.length === 0
                        ? "Sé el primero en hacer una pregunta sobre esta sesión."
                        : "No hay preguntas visibles en este momento."
                      }
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {visibleTab === 'survey' && showSurveyTab && eventCode && (
            <SessionSurvey
              eventCode={eventCode}
              sessionId={sessionConfirmed}
              survey={sessionSurvey}
              isLogisticSupport={isLogisticSupport}
            />
          )}
        </div>

        {/* Botón flotante para agregar pregunta (solo visible en la pestaña de preguntas) */}
        {visibleTab === 'questions' && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton
              onClick={() => {
                // Verificar conexión a internet
                if (!isOnline()) {
                  setToastMessage('No hay conexión a internet. No puede enviar preguntas en modo offline.');
                  setShowToast(true);
                  return;
                }

                // Mostrar el campo de preguntas
                setShowQuestionInput(true);
              }}
              className={showQuestionInput ? '' : 'pulse-animation'}
            >
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </IonFab>
        )}

        {/* Campo de preguntas animado */}
        <QuestionInput
          isOpen={showQuestionInput}
          sessionId={sessionConfirmed}
          onClose={() => setShowQuestionInput(false)}
          onSubmit={handleSubmitQuestion}
          onError={(message) => {
            setToastMessage(message);
            setShowToast(true);
          }}
        />

        {/* Toast para mensajes */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color="danger"
          position="top"
        />
      </IonContent>
    </IonPage>
  );
};

// Función integrada para convertir fecha/hora de inicio y fin, generar el archivo ICS y permitir la descarga
const convertirFechaICS = (fechaHoraInicio: string) => {
  // Simplificamos la función para evitar errores con date-fns
  // Devolvemos un formato básico para el ICS
  return fechaHoraInicio.replace(/-|:|\.\d\d\d/g, "");
}

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state, ownProps) => {
    const sessionId = ownProps.match.params.id;
    console.log('SessionDetail mapStateToProps: ID de sesión:', sessionId);
    console.log('SessionDetail mapStateToProps: Total de sesiones en estado:', state.data.sessions.length);
    console.log('SessionDetail mapStateToProps: Loading:', state.data.loading);
    console.log('🔍 SessionDetail mapStateToProps: Speakers:', {
      totalSpeakers: state.data.speakers.length,
      firstFewSpeakers: state.data.speakers.slice(0, 3).map((s: any) => ({ id: s.id, name: s.name }))
    });

    return {
      session: selectors.getSession(state, ownProps),
      favoriteSessions: state.data.favorites,
      speakers: state.data.speakers,
      loading: state.data.loading
    };
  },
  mapDispatchToProps: {
    addFavorite,
    removeFavorite,
    loadConfData
  },
  component: withRouter(SessionDetail)
});
